import React, { useState, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  ChevronRight, ChevronLeft, Download, CreditCard, 
  CheckCircle, Loader2, FileText
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

// PRSVR Branding
const COLORS = {
  gold: '#c9a961',
  cream: '#faf8f5',
  dark: '#1a1a1a',
  darkGold: '#a88b4a'
};

const STRIPE_LINKS = {
  option1: 'https://invoice.stripe.com/i/acct_1SkvpdFZcd3QPjXw/live_YWNjdF8xU2t2cGRGWmNkM1FQalh3LF9VQW5IeGI0SldSdkR2RGd3cExJeWZMRzk0cUZOaElPLDE2NDQxMDU1OA0200MRTS19G0?s=db',
  option2: 'https://invoice.stripe.com/i/acct_1SkvpdFZcd3QPjXw/live_YWNjdF8xU2t2cGRGWmNkM1FQalh3LF9VQW5oTHdRRlJtMktCcDdOUmlTQmk1ZWpkTmd2YnBBLDE2NDQxMTk1Mg0200ZFETYEcw?s=db'
};

const ClientBrief = () => {
  const [step, setStep] = useState(1); // 1: Cover, 2: Questionnaire, 3: Invoice
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [briefId, setBriefId] = useState(null);
  
  const [formData, setFormData] = useState({
    // Cover
    productVision: '',
    // Questionnaire
    clientName: '',
    companyName: '',
    email: '',
    phone: '',
    projectType: '',
    targetAudience: '',
    timeline: '',
    budget: '',
    additionalNotes: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const nextStep = () => {
    if (step === 1 && !formData.productVision) {
      toast.error('Please describe your product vision');
      return;
    }
    if (step === 2) {
      if (!formData.clientName || !formData.email) {
        toast.error('Please fill in required fields');
        return;
      }
      handleSubmit();
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await api.post('/client-brief/submit', formData);
      if (res.data.success) {
        setBriefId(res.data.brief_id);
        setSubmitted(true);
        setStep(3);
        toast.success('Brief submitted successfully!');
      }
    } catch (error) {
      toast.error('Failed to submit brief');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!briefId) return;
    try {
      const res = await api.get(`/client-brief/${briefId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PRSVR_Brief_${formData.clientName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.cream, fontFamily: "'Montserrat', sans-serif" }}>
      {/* Header */}
      <header className="py-6 px-6 border-b" style={{ borderColor: COLORS.gold }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-wider" style={{ fontFamily: "'Cormorant Garamond', serif", color: COLORS.dark }}>
            PRSVR
          </h1>
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div 
                key={s}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all`}
                style={{ 
                  backgroundColor: step >= s ? COLORS.gold : 'transparent',
                  color: step >= s ? COLORS.cream : COLORS.gold,
                  border: `2px solid ${COLORS.gold}`
                }}
              >
                {s}
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Step 1: Cover */}
        {step === 1 && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: COLORS.dark }}>
                Client Brief Builder
              </h2>
              <p className="text-lg" style={{ color: COLORS.darkGold }}>
                Let's bring your vision to life
              </p>
            </div>

            {/* Shoe Image Placeholder */}
            <div 
              className="aspect-video rounded-lg flex items-center justify-center border-2 border-dashed"
              style={{ borderColor: COLORS.gold, backgroundColor: 'rgba(201, 169, 97, 0.1)' }}
            >
              <div className="text-center p-8">
                <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: COLORS.gold }} />
                <p className="text-lg font-medium" style={{ color: COLORS.dark }}>
                  Custom Footwear Design Brief
                </p>
                <p className="text-sm mt-2" style={{ color: COLORS.darkGold }}>
                  Premium craftsmanship meets your unique vision
                </p>
              </div>
            </div>

            {/* Product Vision */}
            <div>
              <Label 
                className="text-sm font-semibold uppercase tracking-wider mb-2 block"
                style={{ color: COLORS.dark }}
              >
                Your Product Vision *
              </Label>
              <Textarea
                name="productVision"
                value={formData.productVision}
                onChange={handleInputChange}
                placeholder="Describe your ideal product... What inspires you? What story should it tell?"
                className="min-h-[150px] text-base border-2 rounded-lg"
                style={{ 
                  borderColor: COLORS.gold, 
                  backgroundColor: 'white',
                  fontFamily: "'Montserrat', sans-serif"
                }}
              />
            </div>

            <Button 
              onClick={nextStep}
              className="w-full py-6 text-lg font-bold uppercase tracking-wider rounded-lg"
              style={{ backgroundColor: COLORS.gold, color: COLORS.cream }}
            >
              Continue <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Questionnaire */}
        {step === 2 && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: COLORS.dark }}>
                Tell Us About Your Project
              </h2>
              <p style={{ color: COLORS.darkGold }}>
                Help us understand your needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Client Name */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider mb-2 block" style={{ color: COLORS.dark }}>
                  Your Name *
                </Label>
                <Input
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleInputChange}
                  placeholder="Full name"
                  className="border-2 rounded-lg py-3"
                  style={{ borderColor: COLORS.gold }}
                />
              </div>

              {/* Company */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider mb-2 block" style={{ color: COLORS.dark }}>
                  Company / Brand
                </Label>
                <Input
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Company name (optional)"
                  className="border-2 rounded-lg py-3"
                  style={{ borderColor: COLORS.gold }}
                />
              </div>

              {/* Email */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider mb-2 block" style={{ color: COLORS.dark }}>
                  Email *
                </Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                  className="border-2 rounded-lg py-3"
                  style={{ borderColor: COLORS.gold }}
                />
              </div>

              {/* Phone */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider mb-2 block" style={{ color: COLORS.dark }}>
                  Phone
                </Label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  className="border-2 rounded-lg py-3"
                  style={{ borderColor: COLORS.gold }}
                />
              </div>

              {/* Project Type */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider mb-2 block" style={{ color: COLORS.dark }}>
                  Project Type
                </Label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  className="w-full border-2 rounded-lg py-3 px-4"
                  style={{ borderColor: COLORS.gold, backgroundColor: 'white' }}
                >
                  <option value="">Select type...</option>
                  <option value="custom_sneaker">Custom Sneaker Design</option>
                  <option value="leather_goods">Leather Goods</option>
                  <option value="apparel">Apparel Design</option>
                  <option value="full_collection">Full Collection</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Target Audience */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider mb-2 block" style={{ color: COLORS.dark }}>
                  Target Audience
                </Label>
                <Input
                  name="targetAudience"
                  value={formData.targetAudience}
                  onChange={handleInputChange}
                  placeholder="Who is this for?"
                  className="border-2 rounded-lg py-3"
                  style={{ borderColor: COLORS.gold }}
                />
              </div>

              {/* Timeline */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider mb-2 block" style={{ color: COLORS.dark }}>
                  Timeline
                </Label>
                <select
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleInputChange}
                  className="w-full border-2 rounded-lg py-3 px-4"
                  style={{ borderColor: COLORS.gold, backgroundColor: 'white' }}
                >
                  <option value="">Select timeline...</option>
                  <option value="asap">ASAP (Rush)</option>
                  <option value="1_month">Within 1 Month</option>
                  <option value="2_3_months">2-3 Months</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>

              {/* Budget */}
              <div>
                <Label className="text-sm font-semibold uppercase tracking-wider mb-2 block" style={{ color: COLORS.dark }}>
                  Budget Range
                </Label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="w-full border-2 rounded-lg py-3 px-4"
                  style={{ borderColor: COLORS.gold, backgroundColor: 'white' }}
                >
                  <option value="">Select budget...</option>
                  <option value="under_5k">Under $5,000</option>
                  <option value="5k_10k">$5,000 - $10,000</option>
                  <option value="10k_25k">$10,000 - $25,000</option>
                  <option value="25k_plus">$25,000+</option>
                </select>
              </div>
            </div>

            {/* Additional Notes */}
            <div>
              <Label className="text-sm font-semibold uppercase tracking-wider mb-2 block" style={{ color: COLORS.dark }}>
                Additional Notes
              </Label>
              <Textarea
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                placeholder="Any other details we should know..."
                className="min-h-[100px] border-2 rounded-lg"
                style={{ borderColor: COLORS.gold }}
              />
            </div>

            <div className="flex gap-4">
              <Button 
                onClick={prevStep}
                variant="outline"
                className="py-6 px-8 text-lg font-bold uppercase tracking-wider rounded-lg"
                style={{ borderColor: COLORS.gold, color: COLORS.gold }}
              >
                <ChevronLeft className="w-5 h-5 mr-2" /> Back
              </Button>
              <Button 
                onClick={nextStep}
                disabled={loading}
                className="flex-1 py-6 text-lg font-bold uppercase tracking-wider rounded-lg"
                style={{ backgroundColor: COLORS.gold, color: COLORS.cream }}
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</>
                ) : (
                  <>Submit Brief <ChevronRight className="w-5 h-5 ml-2" /></>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Invoice */}
        {step === 3 && (
          <div className="space-y-8">
            <div className="text-center">
              <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: COLORS.gold }} />
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: COLORS.dark }}>
                Brief Submitted!
              </h2>
              <p style={{ color: COLORS.darkGold }}>
                Thank you, {formData.clientName}. Choose your package to proceed.
              </p>
            </div>

            {/* Download PDF */}
            {briefId && (
              <div className="text-center">
                <Button 
                  onClick={handleDownloadPDF}
                  variant="outline"
                  className="py-4 px-8 font-bold uppercase tracking-wider rounded-lg"
                  style={{ borderColor: COLORS.gold, color: COLORS.gold }}
                >
                  <Download className="w-5 h-5 mr-2" /> Download Brief PDF
                </Button>
              </div>
            )}

            {/* Pricing Options */}
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              {/* Option 1 */}
              <div 
                className="p-8 rounded-lg border-2 text-center"
                style={{ borderColor: COLORS.gold, backgroundColor: 'white' }}
              >
                <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: COLORS.dark }}>
                  Consultation Package
                </h3>
                <p className="text-sm mb-4" style={{ color: COLORS.darkGold }}>
                  Strategy session + concept development
                </p>
                <div className="text-4xl font-bold mb-6" style={{ color: COLORS.gold }}>
                  $500
                </div>
                <a href={STRIPE_LINKS.option1} target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="w-full py-5 font-bold uppercase tracking-wider rounded-lg"
                    style={{ backgroundColor: COLORS.gold, color: COLORS.cream }}
                  >
                    <CreditCard className="w-5 h-5 mr-2" /> Pay Now
                  </Button>
                </a>
              </div>

              {/* Option 2 */}
              <div 
                className="p-8 rounded-lg border-2 text-center relative overflow-hidden"
                style={{ borderColor: COLORS.gold, backgroundColor: COLORS.dark }}
              >
                <div 
                  className="absolute top-0 left-0 right-0 py-1 text-xs font-bold uppercase tracking-wider"
                  style={{ backgroundColor: COLORS.gold, color: COLORS.dark }}
                >
                  Recommended
                </div>
                <h3 className="text-2xl font-bold mb-2 mt-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: COLORS.cream }}>
                  Full Design Package
                </h3>
                <p className="text-sm mb-4" style={{ color: COLORS.gold }}>
                  Complete design + production support
                </p>
                <div className="text-4xl font-bold mb-6" style={{ color: COLORS.gold }}>
                  $2,500
                </div>
                <a href={STRIPE_LINKS.option2} target="_blank" rel="noopener noreferrer">
                  <Button 
                    className="w-full py-5 font-bold uppercase tracking-wider rounded-lg"
                    style={{ backgroundColor: COLORS.gold, color: COLORS.dark }}
                  >
                    <CreditCard className="w-5 h-5 mr-2" /> Pay Now
                  </Button>
                </a>
              </div>
            </div>

            {/* Back to start */}
            <div className="text-center mt-8">
              <button 
                onClick={() => { setStep(1); setSubmitted(false); setBriefId(null); setFormData({
                  productVision: '', clientName: '', companyName: '', email: '', phone: '',
                  projectType: '', targetAudience: '', timeline: '', budget: '', additionalNotes: ''
                }); }}
                className="text-sm underline"
                style={{ color: COLORS.darkGold }}
              >
                Start a new brief
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Add Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Montserrat:wght@400;500;600;700&display=swap');
      `}</style>
    </div>
  );
};

export default ClientBrief;
